import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Link2, ArrowUpRight, ShieldCheck, AlertTriangle, RefreshCw, Landmark } from "lucide-react";
import { usePayoutStatus, startPayoutOnboarding, openStripeDashboard } from "../../lib/payouts";
import { isFramed } from "../../lib/externalNav";
import { GrantList } from "../GrantList";
import { PayoutReturnCard } from "./PayoutReturnCard";

const RETURN_KEY = "mra_payout_return_card";

/**
 * PayoutInline — the Stripe Connect step, living inside the Funding need
 * section. Setup is not required to submit; the admin sees its state in the
 * review package. Deep links ?payouts=start|refresh|return still work here.
 */
const COPY = {
  not_started: {
    title: "Connect your Stripe account.",
    body: "Grants are paid through Stripe to a U.S. bank account in your name. Stripe will ask for your legal name, date of birth, home address, the last four digits of your SSN or ITIN, and bank details. My Rich Aunt never sees your banking information. You can submit your campaign for review prior to connecting your Stripe account, but it must be completed before My Rich Aunt can issue grants to you.",
    cta: "Connect Stripe account",
  },
  in_progress: {
    title: "Finish connecting your Stripe account.",
    body: "You started with Stripe but didn't reach the end. Your progress is saved, so pick up where you left off. Grants can't be issued until this is complete.",
    cta: "Continue with Stripe",
  },
  needs_info: {
    title: "Stripe needs a few more details.",
    body: "A few items are still open on your account. Continue to see exactly what Stripe is asking for. Grants can't be issued until this is complete.",
    cta: "Continue with Stripe",
  },
  in_review: {
    title: "Stripe is verifying your details.",
    body: "Nothing more to do here. Verification usually clears within a day, and this status updates on its own.",
    cta: null,
  },
  ready: {
    title: "Your Stripe account is connected.",
    body: "Grants from My Rich Aunt go directly to your bank account through Stripe, typically a few business days after they are sent.",
    cta: "Open Stripe payout dashboard",
  },
};

export const PayoutInline = () => {
  const [params, setParams] = useSearchParams();
  const intent = params.get("payouts");
  const { status, loading, error, refresh } = usePayoutStatus({ returned: intent === "return" });
  const [returnCard, setReturnCard] = useState(() => {
    try { return localStorage.getItem(RETURN_KEY) === "1"; } catch { return false; }
  });
  const dismissReturnCard = () => {
    try { localStorage.removeItem(RETURN_KEY); } catch { /* ignore */ }
    setReturnCard(false);
  };
  const [busy, setBusy] = useState(false);
  const autoFired = useRef(false);

  const launch = async () => {
    setBusy(true);
    try {
      await startPayoutOnboarding();
      if (isFramed()) setBusy(false);   // Stripe opened in a new tab; this page stays interactive
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe couldn't start onboarding. Try again in a moment.");
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!status || !intent || autoFired.current) return;
    autoFired.current = true;
    if (intent === "return") {
      // Warm letter card instead of a grey toast; stays until dismissed and
      // flips to "You're connected." once verification clears.
      try { localStorage.setItem(RETURN_KEY, "1"); } catch { /* ignore */ }
      setReturnCard(true);
      setTimeout(() => document.getElementById("payouts")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    } else if ((intent === "start" || intent === "refresh") && status.state !== "ready" && status.state !== "in_review") {
      launch();
    }
    const next = new URLSearchParams(params);
    next.delete("payouts");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, intent]);

  const state = status?.state || "not_started";
  const copy = COPY[state] || COPY.not_started;
  const ready = state === "ready";

  return (
    <>
    {returnCard && status && (
      <PayoutReturnCard
        state={state}
        onDismiss={dismissReturnCard}
        onBack={() => { dismissReturnCard(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        onSecondary={() => openStripeDashboard().catch((e) => toast.error(e?.response?.data?.detail || "Couldn't open Stripe."))}
      />
    )}
    <div
      id="payouts"
      data-testid="payout-card"
      data-state={state}
      className={`mt-6 rounded-md border p-5 ${ready ? "border-grass/40 bg-grass/[0.05]" : "border-cobalt/30 bg-cobalt/[0.04]"}`}
    >
      <div className="flex items-start gap-4">
        <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sand ${ready ? "bg-grass" : "bg-cobalt"}`}>
          {ready ? <ShieldCheck className="h-5 w-5" strokeWidth={2} /> : <Landmark className="h-5 w-5" strokeWidth={2} />}
        </span>
        <div className="min-w-0 flex-1">
          <span className={`text-[13px] font-semibold uppercase tracking-[0.22em] ${ready ? "text-grass" : "text-cobalt"}`} data-testid="payout-card-eyebrow">
            Stripe Connect{!ready && " · required before grants are issued"}
          </span>
          {loading && !status ? (
            <p className="mt-2 flex items-center gap-2 text-[13px] text-olive" data-testid="payout-card-loading"><Loader2 className="h-4 w-4 animate-spin" /> Checking payout status…</p>
          ) : error ? (
            <p className="mt-2 text-[13px] text-scarlet" data-testid="payout-card-error">{error}</p>
          ) : (
            <>
              <p className="mt-1.5 font-serif text-3xl leading-tight text-ink" data-testid="payout-card-title">{copy.title}</p>
              <p className="mt-2 text-base leading-relaxed text-olive">{copy.body}</p>
              {status?.payout_failed && (
                <p className="mt-3 flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet" data-testid="payout-card-failed">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                  Your last bank deposit failed{status.payout_failure_message ? ` — ${status.payout_failure_message}` : ""}. Update your bank details in Stripe and we'll retry.
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {copy.cta && (
                  <button
                    type="button"
                    data-testid={ready ? "payout-dashboard-btn" : "payout-start-btn"}
                    disabled={busy}
                    onClick={ready ? () => openStripeDashboard().catch((e) => toast.error(e?.response?.data?.detail || "Couldn't open Stripe.")) : launch}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold transition-colors disabled:opacity-60 ${ready ? "border-2 border-ink bg-bone text-ink hover:bg-ink hover:text-sand" : "bg-cobalt text-sand hover:opacity-90"}`}
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : ready ? <ArrowUpRight className="h-4 w-4" strokeWidth={2} /> : <Link2 className="h-4 w-4" strokeWidth={1.75} />}
                    {busy ? "Opening Stripe…" : copy.cta}
                  </button>
                )}
                <button type="button" data-testid="payout-refresh-btn" onClick={() => refresh()} className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink">
                  <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} /> Refresh
                </button>
                {status?.livemode === false && (
                  <span className="rounded-full bg-yellow/40 px-2.5 py-1 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink" data-testid="payout-sandbox-tag">Sandbox</span>
                )}
              </div>
              <GrantList grants={status?.grants || []} onRefresh={() => refresh()} />
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};
